import { useCallback, useEffect, useMemo, useState } from "react";
import { BasketImportDialog } from "./components/BasketImportDialog.js";
import { BasketTabs } from "./components/BasketTabs.js";
import { ChartGrid } from "./components/ChartGrid.js";
import { EmptyState } from "./components/EmptyState.js";
import { Header } from "./components/Header.js";
import { SymbolPicker } from "./components/SymbolPicker.js";
import { QuotesProvider } from "./hooks/QuotesContext.js";
import {
  decodeBasketSharePayload,
  encodeBasketSharePayload,
  parseBasketSnapshot,
  serializeBasket,
  type BasketSnapshot,
} from "./lib/basketTransfer.js";
import { DEFAULT_CHART_SIZE } from "./lib/chartSizes.js";
import { useWorkspace } from "./store/WorkspaceContext.js";
import type {
  CardPlacement,
  GridSize,
  NewGridItem,
  StockBasket,
} from "./store/workspace.js";

export function App() {
  const {
    state,
    addCard,
    removeCard,
    updateCard,
    setLayout,
    createBasket,
    duplicateBasket,
    importBasket,
    selectBasket,
    renameBasket,
    removeBasket,
    toggleTheme,
  } = useWorkspace();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingLayout, setPendingLayout] = useState<
    CardPlacement | undefined
  >();
  const [pendingImport, setPendingImport] = useState<{
    snapshot: BasketSnapshot;
    source: "file" | "link";
  } | null>(null);
  const [transferError, setTransferError] = useState<string | null>(null);

  const activeBasket =
    state.baskets.find((basket) => basket.id === state.activeBasketId) ??
    state.baskets[0]!;
  const symbols = useMemo(
    () =>
      state.baskets.flatMap((basket) =>
        basket.cards.map((card) => card.symbol),
      ),
    [state.baskets],
  );

  const startAdding = useCallback((size: GridSize) => {
    setPendingLayout(size);
    setPickerOpen(true);
  }, []);
  const closePicker = useCallback(() => {
    setPickerOpen(false);
    setPendingLayout(undefined);
  }, []);
  const handlePlace = useCallback((item: NewGridItem) => {
    setPendingLayout(item);
    setPickerOpen(true);
  }, []);

  const handleExport = useCallback(
    (basket: StockBasket) => {
      const blob = new Blob([serializeBasket(basket)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      const filename =
        basket.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "") || "basket";
      anchor.href = url;
      anchor.download = `${filename}.chartandgo.json`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    },
    [],
  );

  const handleShare = useCallback(
    (basket: StockBasket) => {
      const url = new URL(window.location.href);
      url.hash = `basket=${encodeBasketSharePayload(basket)}`;
      return url.toString();
    },
    [],
  );

  const handleImportFile = useCallback(async (file: File) => {
    const snapshot = parseBasketSnapshot(await file.text());
    setPendingImport({ snapshot, source: "file" });
  }, []);

  const clearSharedHash = useCallback(() => {
    if (!window.location.hash.startsWith("#basket=")) return;
    const url = new URL(window.location.href);
    url.hash = "";
    window.history.replaceState(null, "", url);
  }, []);

  // Global keyboard shortcut: Cmd/Ctrl+K opens the symbol picker.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (pickerOpen) {
          closePicker();
        } else {
          startAdding(DEFAULT_CHART_SIZE);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [closePicker, pickerOpen, startAdding]);

  useEffect(() => {
    const readSharedBasket = () => {
      if (!window.location.hash.startsWith("#basket=")) return;
      try {
        const payload = window.location.hash.slice("#basket=".length);
        setPendingImport({
          snapshot: decodeBasketSharePayload(payload),
          source: "link",
        });
      } catch (error) {
        setTransferError((error as Error).message);
        clearSharedHash();
      }
    };
    readSharedBasket();
    window.addEventListener("hashchange", readSharedBasket);
    return () => window.removeEventListener("hashchange", readSharedBasket);
  }, [clearSharedHash]);

  useEffect(() => {
    if (!transferError) return undefined;
    const timeout = window.setTimeout(() => setTransferError(null), 4000);
    return () => window.clearTimeout(timeout);
  }, [transferError]);

  return (
    <QuotesProvider symbols={symbols}>
      <div className="app">
        <Header
          theme={state.theme}
          onToggleTheme={toggleTheme}
          onAdd={startAdding}
        />
        <BasketTabs
          baskets={state.baskets}
          activeBasketId={activeBasket.id}
          onSelect={(id) => {
            closePicker();
            selectBasket(id);
          }}
          onCreate={createBasket}
          onRename={renameBasket}
          onDuplicate={duplicateBasket}
          onExport={handleExport}
          onShare={handleShare}
          onImportFile={handleImportFile}
          onRemove={removeBasket}
        />
        {transferError && (
          <div className="transfer-notice" role="alert">
            {transferError}
          </div>
        )}

        <main className="app-main">
          <div
            className={`workspace-stage ${
              activeBasket.cards.length === 0 ? "workspace-stage--empty" : ""
            }`}
          >
            <ChartGrid
              cards={activeBasket.cards}
              layout={activeBasket.layout}
              theme={state.theme}
              onLayoutChange={setLayout}
              onRemove={removeCard}
              onUpdate={updateCard}
              onPlace={handlePlace}
            />
            {activeBasket.cards.length === 0 && (
              <EmptyState onAdd={() => startAdding(DEFAULT_CHART_SIZE)} />
            )}
          </div>
        </main>

        <SymbolPicker
          open={pickerOpen}
          onClose={closePicker}
          placement={pendingLayout}
          onSelect={(asset) => addCard(asset, pendingLayout)}
        />
        {pendingImport && (
          <BasketImportDialog
            snapshot={pendingImport.snapshot}
            source={pendingImport.source}
            onCancel={() => {
              if (pendingImport.source === "link") clearSharedHash();
              setPendingImport(null);
            }}
            onImport={() => {
              importBasket(pendingImport.snapshot);
              if (pendingImport.source === "link") clearSharedHash();
              setPendingImport(null);
            }}
          />
        )}
      </div>
    </QuotesProvider>
  );
}
